import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  BuildOutlined,
  StorefrontOutlined,
  EventNoteOutlined,
  ExpandMoreRounded,
  AddRounded,
  ArrowOutwardRounded,
  InsightsRounded,
  CalendarMonthRounded,
  ReportProblemOutlined,
} from "@mui/icons-material";
import VehicleStatus from "../VehicleStatus/VehicleStatus";
import RecentComments from "../RecentComments/RecentComments";
import InspectionSubmission from "../InspectionSubmission/InspectionSubmission";
import InspectionSubmission2 from "../InspectionSubmission2/InspectionSubmission2";
import { ReminderTable } from "../ReminderTable/ReminderTable";
import VehicleMap from "../VehicleMap/VehicleMap";
import VehicleAssignmentDashboard from "../VehicleAssignment/VehicleAssignmentDashboard";
import IssuesOverview from "../IssuesOverview/IssuesOverview";
import ActiveWork from "../ActiveWork/ActiveWork";
import {
  WORK_ORDERS,
  WORK_ORDERS_ADD,
  VENDORS,
  REMINDERS_SERVICES,
  ISSUES,
} from "Shared/Constants/routes";
import styles from "./Main.module.scss";

// Operational entry: overdue reminders, unresolved work, then issues. All values
// come from existing APIs. No decorative imagery or simulated urgency metrics.
const Main: React.FC = () => {
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [planningOpen, setPlanningOpen] = useState(false);
  return (
    <div className={styles.dashboard} data-dashboard>
      <header className={styles.intro}>
        <div>
          <h1>Fleet operations</h1>
          <p>Your fleet. Your priorities. One place to act.</p>
        </div>
        <Link className={styles.primaryAction} to={WORK_ORDERS_ADD}>
          <AddRounded aria-hidden="true" /> New work order
        </Link>
      </header>
      <nav className={styles.shortcuts} aria-label="Operational shortcuts">
        <Link className={styles.workShortcut} to={WORK_ORDERS}>
          <span className={styles.shortcutIcon}>
            <BuildOutlined aria-hidden="true" />
          </span>
          <span>
            Work orders<small>Manage active work</small>
          </span>
          <ArrowOutwardRounded
            className={styles.shortcutArrow}
            aria-hidden="true"
          />
        </Link>
        <Link className={styles.reminderShortcut} to={REMINDERS_SERVICES}>
          <span className={styles.shortcutIcon}>
            <EventNoteOutlined aria-hidden="true" />
          </span>
          <span>
            Service reminders<small>Plan the next service</small>
          </span>
          <ArrowOutwardRounded
            className={styles.shortcutArrow}
            aria-hidden="true"
          />
        </Link>
        <Link className={styles.issueShortcut} to={ISSUES}>
          <span className={styles.shortcutIcon}>
            <ReportProblemOutlined aria-hidden="true" />
          </span>
          <span>
            Issues<small>Review reported problems</small>
          </span>
          <ArrowOutwardRounded
            className={styles.shortcutArrow}
            aria-hidden="true"
          />
        </Link>
        <Link className={styles.vendorShortcut} to={VENDORS}>
          <span className={styles.shortcutIcon}>
            <StorefrontOutlined aria-hidden="true" />
          </span>
          <span>
            Find a vendor<small>Explore service partners</small>
          </span>
          <ArrowOutwardRounded
            className={styles.shortcutArrow}
            aria-hidden="true"
          />
        </Link>
      </nav>
      <div className={styles.priorityGrid}>
        <section className={styles.widget} aria-label="Priority reminders">
          <ReminderTable />
        </section>
        <section className={styles.widget} aria-label="Vehicle status">
          <VehicleStatus />
        </section>
      </div>
      <div className={styles.contextGrid}>
        <ActiveWork />
        <IssuesOverview />
      </div>
      <details
        className={styles.secondary}
        onToggle={(event) => setInsightsOpen(event.currentTarget.open)}
      >
        <summary>
          <InsightsRounded className={styles.sectionIcon} aria-hidden="true" />
          <div>
            <h2>Fleet insights</h2>
            <p>Inspection trends, locations, and recent comments</p>
          </div>
          <ExpandMoreRounded aria-hidden="true" />
        </summary>
        {insightsOpen && (
          <div className={styles.insightsGrid}>
            <InspectionSubmission />
            <InspectionSubmission2 />
            <VehicleMap className={styles.locationMap} />
            <RecentComments />
          </div>
        )}
      </details>
      <details
        className={styles.secondary}
        onToggle={(event) => setPlanningOpen(event.currentTarget.open)}
      >
        <summary>
          <CalendarMonthRounded
            className={styles.sectionIcon}
            aria-hidden="true"
          />
          <div>
            <h2>Vehicle assignments</h2>
            <p>Review the assignment schedule</p>
          </div>
          <ExpandMoreRounded aria-hidden="true" />
        </summary>
        {planningOpen && (
          <div className={styles.planningContent}>
            <VehicleAssignmentDashboard />
          </div>
        )}
      </details>
    </div>
  );
};
export default Main;
